import cn from 'classnames'
import { useTranslation } from 'react-i18next'
import { useCallback, useState, useEffect } from 'react'
import { formatNumbers } from 'core/helpers/format-numbers'
import { apiClient } from 'core/http'
import { toast } from 'react-toastify'
import { socketClient } from 'core/services'
import { useAutoTranslate } from 'core/hooks/useAutoTranslate'

import c from './assets/sass/item.module.scss'

export const Item = ({
    id,
    type,
    imageUrl,
    task,
    reward,
    energy,
    status,
    url,
    taskType,
    requirement,
    progress,
    onUpdate,
    onComplete,
}) => {
    const { t } = useTranslation()
    const [state, setState] = useState(status)
    const [wait, setWait] = useState(null)
    const [currentProgress, setCurrentProgress] = useState(progress || 0)
    
    // Автоперевод названия задания
    const translatedTask = useAutoTranslate(task)

    // WebSocket обновление прогресса для текущего задания
    useEffect(() => {
        const handleTaskProgress = (data) => {
            if (data.taskId === id) {
                console.log(`📊 Task ${id} progress updated:`, data.progress)
                setCurrentProgress(data.progress)
            }
        }

        socketClient.on('task_progress_updated', handleTaskProgress)

        return () => {
            socketClient.off('task_progress_updated', handleTaskProgress)
        }
    }, [id])

    // Обновляем прогресс если приходит новое значение из пропсов
    useEffect(() => {
        if (progress !== undefined) {
            setCurrentProgress(progress)
        }
    }, [progress])

    const getRewardMessage = (coins, energyReward) => {
        const parts = []
        if (coins > 0) {
            parts.push(`+${formatNumbers(coins)} ${t('tasks.item.coins')}`)
        }
        if (energyReward > 0) {
            parts.push(`+${energyReward} ${t('tasks.item.energy')}`)
        }
        if (parts.length === 0) {
            return t('tasks.item.completed')
        }
        return `${t('tasks.item.completed')} ${parts.join(' ')}`
    }

    const getRequiredCount = useCallback(() => {
        if (!requirement) return null
        if (taskType === 'DAILY_LOGIN')
            return requirement.days || requirement.consecutiveDays
        if (taskType === 'REFER_FRIENDS') return requirement.count
        if (taskType === 'TAP_ENERGY') return requirement.tapsCount
        if (taskType === 'PLAY_MINES') return requirement.gamesCount
        if (taskType === 'PLAY_BATTLE') return requirement.battlesCount
        if (taskType === 'OPEN_CASES')
            return requirement.casesCount || requirement.openCount
        return null
    }, [requirement, taskType])

    const onClick = useCallback(async () => {
        if (state === 'done') return

        try {
            // Для заданий с прогрессом - проверяем только если условия выполнены
            const requiredCount = getRequiredCount()
            const canClaim = requiredCount && currentProgress >= requiredCount

            if (
                [
                    'TAP_ENERGY',
                    'PLAY_MINES',
                    'PLAY_BATTLE',
                    'OPEN_CASES',
                    'REFER_FRIENDS',
                ].includes(taskType)
            ) {
                // Если условия выполнены - забираем награду
                if (canClaim) {
                    const checkRes = await apiClient.post(`/tasks/${id}/check`)
                    if (checkRes?.data?.completed) {
                        setState('done')
                        const serverReward = Number(
                            checkRes.data.reward || reward
                        )
                        const serverEnergy = Number(
                            checkRes.data.energyReward || energy || 0
                        )
                        const rewardMsg = getRewardMessage(
                            serverReward,
                            serverEnergy
                        )
                        toast(rewardMsg, { type: 'success' })
                        if (onComplete) onComplete()
                        if (onUpdate) setTimeout(onUpdate, 500)
                    } else if (checkRes?.data?.progress !== undefined) {
                        setCurrentProgress(checkRes.data.progress)
                        // Показываем toast если backend прислал
                        if (
                            checkRes.data.showToast &&
                            checkRes.data.toastMessage
                        ) {
                            toast(checkRes.data.toastMessage, { type: 'info' })
                        }
                    }
                } else {
                    // Если условия не выполнены - обновляем прогресс
                    const checkRes = await apiClient.post(`/tasks/${id}/check`)
                    if (checkRes?.data?.progress !== undefined) {
                        setCurrentProgress(checkRes.data.progress)
                    }
                    // Показываем toast если backend прислал
                    if (
                        checkRes?.data?.showToast &&
                        checkRes?.data?.toastMessage
                    ) {
                        toast(checkRes.data.toastMessage, { type: 'info' })
                    }
                }
                return
            }

            // Стартуем задание на бекенде
            await apiClient.post(`/tasks/${id}/start`)

            // Для TELEGRAM_CHANNEL - открываем канал перед проверкой
            if (taskType === 'TELEGRAM_CHANNEL' && url) {
                try {
                    if (window?.Telegram?.WebApp?.openTelegramLink) {
                        window.Telegram.WebApp.openTelegramLink(url)
                    } else if (window?.Telegram?.WebApp?.openLink) {
                        window.Telegram.WebApp.openLink(url)
                    } else {
                        window.open(url, '_blank')
                    }
                } catch (e) {
                    console.error('Failed to open Telegram channel:', e)
                }
            }

            // Для внешних ссылок - открываем URL
            if (taskType === 'EXTERNAL_LINK' && url) {
                try {
                    let isTelegramLink = false
                    try {
                        const u = new URL(url)
                        isTelegramLink = u.hostname.toLowerCase() === 't.me'
                    } catch {}
                    if (
                        isTelegramLink &&
                        window?.Telegram?.WebApp?.openTelegramLink
                    ) {
                        window.Telegram.WebApp.openTelegramLink(url)
                    } else if (window?.Telegram?.WebApp?.openLink) {
                        window.Telegram.WebApp.openLink(url)
                    } else {
                        window.open(url, '_blank')
                    }
                } catch {}
            }

            // Проверяем выполнение задания для типов с проверкой подписки/ссылок
            if (
                ['TELEGRAM_CHANNEL', 'EXTERNAL_LINK', 'DAILY_LOGIN'].includes(
                    taskType
                )
            ) {
                const check = async () => {
                    try {
                        const res = await apiClient.post(`/tasks/${id}/check`)
                        if (res?.data?.completed) {
                            setState('done')
                            setWait(null)
                            setCurrentProgress(res?.data?.progress || 0)
                            const serverReward = Number(
                                res.data.reward || reward
                            )
                            const serverEnergy = Number(
                                res.data.energyReward || energy || 0
                            )
                            const rewardMsg = getRewardMessage(
                                serverReward,
                                serverEnergy
                            )
                            toast(rewardMsg, { type: 'success' })
                            if (onComplete) onComplete()
                            if (onUpdate) setTimeout(onUpdate, 500)
                        } else if (typeof res?.data?.waitSeconds === 'number') {
                            setWait(res.data.waitSeconds)
                            setTimeout(
                                check,
                                Math.min(15000, res.data.waitSeconds * 1000)
                            )
                        } else if (typeof res?.data?.progress === 'number') {
                            setCurrentProgress(res.data.progress)
                        }

                        // Показываем toast если backend прислал (условия не выполнены)
                        if (
                            res?.data?.showToast &&
                            res?.data?.toastMessage &&
                            !res?.data?.completed
                        ) {
                            toast(res.data.toastMessage, { type: 'warning' })
                        }
                    } catch (checkError) {
                        console.error('Task check error:', checkError)
                        // Показываем toast с ошибкой если есть
                        if (
                            checkError.response?.data?.showToast &&
                            checkError.response?.data?.toastMessage
                        ) {
                            toast(checkError.response.data.toastMessage, {
                                type: 'error',
                            })
                        }
                    }
                }

                // Разные задержки для разных типов заданий
                const delay = taskType === 'TELEGRAM_CHANNEL' ? 3000 : 1000
                setTimeout(check, delay)
            }
        } catch (error) {
            toast(
                t('tasks.item.error', {
                    error: error.response?.data?.error || error.message,
                }),
                { type: 'error' }
            )
        }
    }, [
        id,
        url,
        state,
        taskType,
        reward,
        energy,
        onUpdate,
        onComplete,
        currentProgress,
        getRequiredCount,
    ])

    const requiredCount = getRequiredCount()
    const isReady =
        requiredCount && currentProgress >= requiredCount && state !== 'done'

    return (
        <li
            className={cn(c.component, {
                [c.telegram]: type === 'telegram',
                [c.instagram]: type === 'instagram',
                [c.youtube]: type === 'youtube',
                [c.tiktok]: type === 'tiktok',
                [c.custom]: type === 'custom',
                [c.done]: state === 'done',
                [c.checking]: wait !== null && state !== 'done',
                [c.ready]: isReady,
            })}
            style={
                type === 'custom' && imageUrl
                    ? {
                          '--custom-icon-url': `url(${imageUrl})`,
                      }
                    : undefined
            }
            onClick={onClick}
        >
            <div className={c.row}>
                <div className={c.titleRow}>
                    <span className={c.title}>
                        {translatedTask}
                        {isReady && (
                            <span
                                style={{
                                    marginLeft: '8px',
                                    color: '#4CFF2B',
                                    fontSize: '11px',
                                }}
                            >
                                {t('tasks.item.ready')}
                            </span>
                        )}
                    </span>
                    {requiredCount && state !== 'done' && (
                        <span className={c.progress}>
                            {currentProgress}/{requiredCount}
                        </span>
                    )}
                </div>
                <div className={c.details}>
                    <span className={c.reward}>{formatNumbers(reward)}</span>
                    {energy > 0 && <span className={c.energy}>{energy}</span>}
                </div>
            </div>
            {wait !== null && state !== 'done' ? (
                <div className={c.checking} />
            ) : null}
        </li>
    )
}
